/**
 * SectionCard - Reusable Card Component for Input Sections
 *
 * Provides consistent styling and behavior for workflow input sections.
 * Features status indicators, validation states, and collapsible behavior.
 *
 * Usage:
 * ```tsx
 * <SectionCard
 *   title="Project Dates"
 *   status="complete"
 *   isRequired
 *   showValidation={showValidation}
 *   validationErrors={errors}
 * >
 *   <DateInputs ... />
 * </SectionCard>
 * ```
 */

import { Badge, Box, Heading, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import { AlertCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';

export type SectionStatus = 'empty' | 'partial' | 'complete';

export interface SectionCardProps {
  /** Section title */
  title: string;

  /** Optional description text */
  description?: string;

  /** Status indicator */
  status?: SectionStatus;

  /** Show required indicator */
  isRequired?: boolean;

  /** Disable interaction */
  isDisabled?: boolean;

  /** Show validation errors */
  showValidation?: boolean;

  /** Validation error messages */
  validationErrors?: string[];

  /** Validation warning messages (shown in yellow, don't block submission) */
  validationWarnings?: string[];

  /** Section content */
  children: React.ReactNode;

  /** Initial expanded state */
  defaultExpanded?: boolean;

  /** Controlled expanded state (overrides internal state if provided) */
  isExpanded?: boolean;

  /** Callback when expansion state changes (for controlled mode) */
  onExpandedChange?: (expanded: boolean) => void;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  description,
  status = 'empty',
  isRequired = false,
  isDisabled = false,
  showValidation = false,
  validationErrors = [],
  validationWarnings = [],
  children,
  defaultExpanded,
  isExpanded: controlledExpanded,
  onExpandedChange,
}) => {
  // Default to closed if empty, open if partial/complete (unless explicitly set)
  const initialExpanded = defaultExpanded !== undefined ? defaultExpanded : status !== 'empty';
  const [internalExpanded, setInternalExpanded] = useState(initialExpanded);

  // Use controlled state if provided, otherwise use internal state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleExpandedChange = (newExpanded: boolean) => {
    if (controlledExpanded === undefined) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  };

  // Get status badge configuration
  const getStatusBadge = () => {
    switch (status) {
      case 'complete':
        return { label: 'Complete', colorPalette: 'green' as const };
      case 'partial':
        return { label: 'In Progress', colorPalette: 'orange' as const };
      default:
        return null;
    }
  };

  // Get left border color based on status
  const getBorderColor = () => {
    switch (status) {
      case 'complete':
        return 'green.500';
      case 'partial':
        return 'orange.400';
      default:
        return 'gray.200';
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    if (showValidation && validationErrors.length > 0) {
      return (
        <Icon color="red.500">
          <AlertCircle size={16} />
        </Icon>
      );
    }
    if (status === 'complete') {
      return (
        <Icon color="green.500">
          <CheckCircle size={16} />
        </Icon>
      );
    }
    return null;
  };

  const statusBadge = getStatusBadge();
  const borderColor = getBorderColor();
  const hasErrors = showValidation && validationErrors.length > 0;
  const hasWarnings = validationWarnings.length > 0;

  return (
    <Box
      bg="white"
      borderRadius={LAYOUT_SPACING.BORDER_RADIUS_LG}
      border="1px solid"
      borderColor="gray.200"
      borderLeft="4px solid"
      borderLeftColor={borderColor}
      boxShadow="sm"
      overflow="hidden"
      opacity={isDisabled ? 0.6 : 1}
      transition={`all ${LAYOUT_SPACING.TRANSITION_FAST}`}
    >
      {/* Header */}
      <Box
        p={LAYOUT_SPACING.CARD_PADDING}
        borderBottom={isExpanded ? '1px solid' : 'none'}
        borderColor="gray.200"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        onClick={() => !isDisabled && handleExpandedChange(!isExpanded)}
        _hover={!isDisabled ? { bg: 'gray.50' } : {}}
        transition={`background-color ${LAYOUT_SPACING.TRANSITION_FAST}`}
      >
        <HStack justify="space-between" align="center" gap={LAYOUT_SPACING.ELEMENT_GAP}>
          <HStack gap={LAYOUT_SPACING.ELEMENT_GAP} flex={1}>
            {/* Status Icon */}
            {getStatusIcon()}

            {/* Title */}
            <Heading size="md" color="gray.800">
              {title}
              {isRequired && (
                <Text as="span" color="red.500" ml={1}>
                  *
                </Text>
              )}
            </Heading>

            {/* Status Badge */}
            {statusBadge && (
              <Badge colorPalette={statusBadge.colorPalette} size="sm">
                {statusBadge.label}
              </Badge>
            )}
          </HStack>

          {/* Expand/Collapse Button */}
          <IconButton
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) handleExpandedChange(!isExpanded);
            }}
            disabled={isDisabled}
            tabIndex={-1}
          >
            <Icon>{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</Icon>
          </IconButton>
        </HStack>

        {/* Description */}
        {description && (
          <Text mt={LAYOUT_SPACING.ELEMENT_GAP} fontSize="sm" color="gray.600">
            {description}
          </Text>
        )}

        {/* Collapsed Status Messages */}
        {!isExpanded && (hasErrors || hasWarnings) && (
          <VStack align="start" gap={1} mt={LAYOUT_SPACING.ELEMENT_GAP}>
            {validationErrors.map((error) => (
              <Text key={error} fontSize="sm" color="red.600">
                • {error}
              </Text>
            ))}
            {validationWarnings.map((warning) => (
              <Text key={warning} fontSize="sm" color="yellow.700">
                ⚠ {warning}
              </Text>
            ))}
          </VStack>
        )}
      </Box>

      {/* Content */}
      {isExpanded && (
        <Box p={LAYOUT_SPACING.CARD_PADDING}>
          {children}

          {/* Expanded Validation Errors */}
          {hasErrors && (
            <Box
              mt={LAYOUT_SPACING.CONTENT_GAP}
              p={LAYOUT_SPACING.CARD_PADDING_SM}
              bg="red.50"
              borderRadius={LAYOUT_SPACING.BORDER_RADIUS}
              border="1px solid"
              borderColor="red.200"
            >
              <Text
                fontSize="sm"
                color="red.700"
                fontWeight="medium"
                mb={LAYOUT_SPACING.ELEMENT_GAP}
              >
                Please fix the following errors:
              </Text>
              <VStack align="start" gap={1}>
                {validationErrors.map((error) => (
                  <Text key={error} fontSize="sm" color="red.600">
                    • {error}
                  </Text>
                ))}
              </VStack>
            </Box>
          )}

          {/* Expanded Validation Warnings */}
          {hasWarnings && (
            <Box
              mt={LAYOUT_SPACING.CONTENT_GAP}
              p={LAYOUT_SPACING.CARD_PADDING_SM}
              bg="yellow.50"
              borderRadius={LAYOUT_SPACING.BORDER_RADIUS}
              border="1px solid"
              borderColor="yellow.300"
            >
              <Text
                fontSize="sm"
                color="yellow.800"
                fontWeight="medium"
                mb={LAYOUT_SPACING.ELEMENT_GAP}
              >
                Note: The following are informational warnings:
              </Text>
              <VStack align="start" gap={1}>
                {validationWarnings.map((warning) => (
                  <Text key={warning} fontSize="sm" color="yellow.700">
                    ⚠ {warning}
                  </Text>
                ))}
              </VStack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
